import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import {
  registerCorePack,
  setActiveProjectId,
  setMapPersistence,
  setPosePersistence,
  useMapEditorStore,
  type StudioProject,
} from '../../engine';
import { ensureLibraryLoaded } from '../assets/libraryLoader';
import { registerProjectAssets } from '../assets/registerProjectAsset';
import { registerProjectCharacters } from '../characters/registerProjectCharacters';
import { buildKitMapPersistence, buildKitPosePersistence } from '../persistence/buildKitPersistence';
import { loadProject } from '../persistence/projectRepository';
import { setWeaponPersistence } from '../gunsmith/store/weaponPersistence';
import { createStudioApiWeaponPersistence } from '../gunsmith/store/studioApiWeaponPersistence';

interface ProjectContextValue {
  project: StudioProject;
  setProject: (project: StudioProject) => void;
  reload: () => Promise<void>;
}

// Exported so a dev-only fixture provider (see src/studio/dev) can supply a
// value through the same context without loading a project from the API.
export const ProjectContext = createContext<ProjectContextValue | null>(null);

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; project: StudioProject };

/**
 * Loads a project and wires the engine to it: registers the core pack, installs
 * the API-backed persistence transports (map, pose and weapon), sets the
 * active project id, registers the project's uploaded asset and custom
 * character/rig definitions, and hydrates the map-editor store.
 */
export function ProjectProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    registerCorePack();
    setMapPersistence(buildKitMapPersistence);
    setPosePersistence(buildKitPosePersistence);
    setWeaponPersistence(createStudioApiWeaponPersistence());
    setActiveProjectId(projectId);

    loadProject(projectId)
      .then((project) => {
        if (cancelled) return;
        if (!project) {
          setState({ status: 'error', message: 'Project not found.' });
          return;
        }
        registerProjectAssets(project.assets);
        registerProjectCharacters(project.characters, project.rigBones);
        // The shared CC0 model library registers for projects with a 3D
        // scene; 2D projects route to their own asset kinds when those land.
        // Fire-and-forget: definitions appear when the manifest arrives and
        // Studio works without it.
        if (project.config.projection !== '2D') void ensureLibraryLoaded();
        useMapEditorStore.getState().hydrateFromDocument(project.map);
        setState({ status: 'ready', project });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load project.' });
      });

    return () => {
      cancelled = true;
      setActiveProjectId(null);
    };
  }, [projectId, retryNonce]);

  if (state.status === 'loading') {
    return <div className="studio-loading">Loading project…</div>;
  }
  if (state.status === 'error') {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 18, padding: '0 40px', textAlign: 'center', background: 'var(--ss-bg)', color: 'var(--ss-text)',
      }}>
        <h1 style={{ fontFamily: 'var(--ss-display)', fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>Couldn't load this project</h1>
        <p style={{ fontSize: 14.5, color: 'var(--ss-text-2)', maxWidth: 420, lineHeight: 1.6 }}>{state.message}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="ss-btn ss-btn--ghost" onClick={() => setRetryNonce((n) => n + 1)}>Retry</button>
          <a className="ss-btn ss-btn--primary" href="/app">Back to dashboard</a>
        </div>
      </div>
    );
  }

  const value: ProjectContextValue = {
    project: state.project,
    setProject: (project) => setState({ status: 'ready', project }),
    reload: async () => {
      const next = await loadProject(projectId);
      if (next) {
        registerProjectAssets(next.assets);
        registerProjectCharacters(next.characters, next.rigBones);
        setState({ status: 'ready', project: next });
      }
    },
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within a ProjectProvider.');
  return ctx;
}
